import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import type { CustomDomainDnsRecord } from '@/api/customDomains/customDomainsApi.types';
import { copyTextToClipboard } from '@/lib/clipboard';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

interface CustomDomainDnsRecordRowProps {
  record: CustomDomainDnsRecord;
}

interface CopyableFieldProps {
  label: string;
  text: string;
}

function CopyableField({ label, text }: CopyableFieldProps) {
  const { t } = useTranslation('customDomains');

  async function copyText() {
    const didCopy = await copyTextToClipboard(text);
    if (didCopy === true) {
      showSuccessNotification(t('dns.copied'));
      return;
    }
    showErrorNotification(t('dns.copyFailed'));
  }

  return (
    <div className="min-w-0">
      <div className="text-[11px] font-bold text-zoomuted">{label}</div>
      <div className="flex items-center gap-1.5 min-w-0">
        <code className="text-xs text-zooindigo break-all">{text}</code>
        <Button
          type="button"
          variant="ghost"
          size="chip"
          className="shrink-0"
          aria-label={t('dns.copyAccessibleLabel', { label })}
          title={t('dns.copy')}
          onClick={copyText}
        >
          <Icon name="Copy" />
        </Button>
      </div>
    </div>
  );
}

export function CustomDomainDnsRecordRow({ record }: CustomDomainDnsRecordRowProps) {
  const { t } = useTranslation('customDomains');

  return (
    <div className="rounded-xl border border-zooborder bg-zoooffwhite p-3">
      <div className="text-xs font-bold text-zooindigo mb-2">
        {t('dns.purpose.' + record.purpose)}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[5rem_minmax(0,1fr)_minmax(0,2fr)] gap-3">
        <div>
          <div className="text-[11px] font-bold text-zoomuted">{t('dns.typeLabel')}</div>
          <code className="text-xs text-zooindigo">{record.recordType}</code>
        </div>
        <CopyableField label={t('dns.nameLabel')} text={record.name} />
        <CopyableField label={t('dns.valueLabel')} text={record.value} />
      </div>
    </div>
  );
}
